import * as React from 'react'

import { DateTime } from 'luxon'

type UseCountdown = {
  isLocked: boolean
  totalSeconds: number
  label: string
}

export function useCountdown(untilISO?: string | null): UseCountdown {
  const until = React.useMemo(
    () => (untilISO ? DateTime.fromISO(untilISO).toUTC() : null),
    [untilISO]
  )

  const initial = React.useMemo(() => {
    if (!until) return 0
    return Math.max(
      0,
      Math.floor(until.toSeconds() - DateTime.utc().toSeconds())
    )
  }, [until])

  const [remaining, setRemaining] = React.useState(initial)

  React.useEffect(() => {
    if (!until) return

    let timeoutId: ReturnType<typeof setTimeout> | undefined
    let rafId = 0

    const tick = () => {
      const now = DateTime.utc()
      const next = Math.max(0, Math.floor(until.toSeconds() - now.toSeconds()))
      // evita re-render se não mudou o segundo
      setRemaining(prev => (prev !== next ? next : prev))

      // agenda exatamente no próximo boundary de segundo
      const msUntilNextSecond = 1000 - (now.toMillis() % 1000)
      timeoutId = setTimeout(() => {
        rafId = requestAnimationFrame(tick)
      }, msUntilNextSecond)
    }

    tick()
    return () => {
      if (timeoutId) clearTimeout(timeoutId)
      if (rafId) cancelAnimationFrame(rafId)
    }
  }, [until])

  const format = React.useCallback((total: number) => {
    const days = Math.floor(total / 86400)
    const hours = Math.floor((total % 86400) / 3600)
    const minutes = Math.floor((total % 3600) / 60)
    const seconds = total % 60
    const pad = (n: number) => String(n).padStart(2, '0')
    const base = `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`
    return days > 0 ? `${days}d ${base}` : base
  }, [])

  if (!untilISO) return { isLocked: false, totalSeconds: 0, label: '' }

  return {
    isLocked: !!until && remaining > 0,
    totalSeconds: remaining,
    label: format(remaining)
  }
}
